<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {BlogPostLayout, Hero} from '@react-spectrum/docs';
export default BlogPostLayout;

---
description: After many months of research, development, and testing, we’re excited to announce that the React Spectrum [ComboBox](../react-spectrum/ComboBox.html) component and React Aria [useComboBox](../react-aria/useComboBox.html) hook are now available!
date: 2021-07-12
---

# July 12, 2021 Release

After many months of research, development, and extensive testing across browsers, devices, and assistive technology, we’re excited to announce that the React Spectrum [ComboBox](/react-spectrum/ComboBox.html) component and React Aria [useComboBox](/react-aria/useComboBox.html) hook are now available! We’ve focused on the following areas to help you build quality autocomplete experiences.

- Accessibility — Our ComboBox has been tested with screen readers across desktop and mobile devices, and with many different input methods including mouse, touch, and keyboard. We encountered many screen reader differences, and worked hard to ensure announcements are clear and consistent.
- Mobile — On small screens, the React Spectrum ComboBox is automatically displayed in a tray, which improves the user experience by giving them a larger area to scroll. We also optimized our experience for touch screen interactions and on screen keyboards.
- Asynchronous loading — Autocomplete suggestions can be loaded asynchronously, and large lists can be loaded on demand through infinite scrolling.
- Customizability — React Aria hooks allow full control over the rendering and styling of your ComboBox component, while letting us take care of the behavioral complexities for you. Use our default filter or you can provide custom filtering for complete control.

## New component

- **ComboBox**
    - `@react-spectrum/combobox` - [Docs](https://react-spectrum.adobe.com/react-spectrum/ComboBox.html)
    - `@react-aria/combobox` - [Docs](https://react-spectrum.adobe.com/react-aria/useComboBox.html)
    - `@react-stately/combobox` - [Docs](https://react-spectrum.adobe.com/react-stately/useComboBoxState.html)

## Released packages

```
- @adobe/react-spectrum@3.12.0
- @react-aria/aria-modal-polyfill@3.3.2
- @react-aria/combobox@3.0.0
- @react-aria/listbox@3.3.0
- @react-aria/overlays@3.7.1
- @react-aria/searchfield@3.1.4
- @react-aria/select@3.4.0
- @react-spectrum/combobox@3.0.0
- @react-spectrum/listbox@3.5.0
- @react-spectrum/picker@3.3.0
- @react-stately/combobox@3.0.0
- @react-types/combobox@3.0.0
- @react-types/listbox@3.2.0
- @react-types/select@3.3.0
- react-aria@3.8.0
- react-stately@3.7.0
```
